/**
 * Import/Export Utilities
 *
 * Allows users to export all their data to a JSON file and import it back.
 * Useful for backup, migration, and transferring data between browsers.
 */

import { db } from '@/db/database';

export interface ExportData {
  version: string;
  exportedAt: string;
  data: {
    quizConfigs: any[];
    quizSessions: any[];
    knowledgeStates: any[];
  };
}

/**
 * Export all user data to a downloadable JSON file
 */
export async function exportData(): Promise<void> {
  try {
    console.log('📦 Exporting data...');

    // Fetch all data from IndexedDB
    const [quizConfigs, quizSessions, knowledgeStates] = await Promise.all([
      db.quiz_configs.toArray(),
      db.quiz_sessions.toArray(),
      db.knowledge_state.toArray(),
    ]);

    // Create export object
    const exportData: ExportData = {
      version: '2.0',
      exportedAt: new Date().toISOString(),
      data: {
        quizConfigs,
        quizSessions,
        knowledgeStates,
      },
    };

    // Convert to JSON
    const jsonString = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });

    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `mapquizzr-backup-${new Date().toISOString().split('T')[0]}.json`;

    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Cleanup
    URL.revokeObjectURL(url);

    console.log('✅ Export complete!');
    console.log(`Exported: ${quizConfigs.length} configs, ${quizSessions.length} sessions, ${knowledgeStates.length} knowledge states`);
  } catch (error) {
    console.error('Failed to export data:', error);
    throw error;
  }
}

/**
 * Import data from a JSON file
 * @param file The JSON file to import
 * @param mergeStrategy How to handle conflicts ('replace' | 'merge')
 */
export async function importData(
  file: File,
  mergeStrategy: 'replace' | 'merge' = 'merge'
): Promise<{ success: boolean; stats: { configs: number; sessions: number; knowledge: number } }> {
  try {
    console.log(`📥 Importing data (strategy: ${mergeStrategy})...`);

    // Read file
    const text = await file.text();
    const importData: ExportData = JSON.parse(text);

    // Validate format
    if (!importData.version || !importData.data) {
      throw new Error('Invalid export file format');
    }

    const stats = {
      configs: 0,
      sessions: 0,
      knowledge: 0,
    };

    if (mergeStrategy === 'replace') {
      // Clear all existing data
      await db.quiz_configs.clear();
      await db.quiz_sessions.clear();
      await db.knowledge_state.clear();

      // Add all imported data
      await db.quiz_configs.bulkAdd(importData.data.quizConfigs);
      await db.quiz_sessions.bulkAdd(importData.data.quizSessions);
      await db.knowledge_state.bulkAdd(importData.data.knowledgeStates);

      stats.configs = importData.data.quizConfigs.length;
      stats.sessions = importData.data.quizSessions.length;
      stats.knowledge = importData.data.knowledgeStates.length;
    } else {
      // Merge strategy: add new records, update existing based on timestamps

      // Import quiz configs (keep most recent lastUsedAt)
      for (const config of importData.data.quizConfigs) {
        const existing = await db.quiz_configs.get(config.signature);
        if (!existing || new Date(config.lastUsedAt) > new Date(existing.lastUsedAt)) {
          await db.quiz_configs.put(config);
          stats.configs++;
        }
      }

      // Import sessions (add if not duplicate)
      for (const session of importData.data.quizSessions) {
        const existing = await db.quiz_sessions.get(session.id);
        if (!existing) {
          await db.quiz_sessions.put(session);
          stats.sessions++;
        }
      }

      // Import knowledge states (merge attempts)
      for (const state of importData.data.knowledgeStates) {
        const existing = await db.knowledge_state.get(state.id);

        if (!existing) {
          await db.knowledge_state.put(state);
          stats.knowledge++;
        } else {
          // Merge: keep best performance
          const merged = {
            ...existing,
            completedPrompts: Math.max(existing.completedPrompts, state.completedPrompts),
            attemptsCount: Math.max(existing.attemptsCount, state.attemptsCount),
            lastUpdated:
              new Date(state.lastUpdated) > new Date(existing.lastUpdated)
                ? state.lastUpdated
                : existing.lastUpdated,
          };
          await db.knowledge_state.put(merged);
          stats.knowledge++;
        }
      }
    }

    console.log('✅ Import complete!');
    console.log(`Imported: ${stats.configs} configs, ${stats.sessions} sessions, ${stats.knowledge} knowledge states`);

    return { success: true, stats };
  } catch (error) {
    console.error('Failed to import data:', error);
    throw error;
  }
}

/**
 * Validate an export file without importing it
 */
export async function validateExportFile(file: File): Promise<{
  valid: boolean;
  error?: string;
  stats?: { configs: number; sessions: number; knowledge: number };
}> {
  try {
    const text = await file.text();
    const data: ExportData = JSON.parse(text);

    if (!data.version || !data.data) {
      return { valid: false, error: 'Invalid file format' };
    }

    return {
      valid: true,
      stats: {
        configs: data.data.quizConfigs?.length || 0,
        sessions: data.data.quizSessions?.length || 0,
        knowledge: data.data.knowledgeStates?.length || 0,
      },
    };
  } catch (error) {
    return {
      valid: false,
      error: error instanceof Error ? error.message : 'Failed to parse file',
    };
  }
}
